import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { DialogService, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TrackedDialogService } from '../../../core/services/tracked-dialog.service';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';
import { PracticesFormComponent } from './practices-form/practices-form.component';
import { PracticesStateService } from './practices-state.service';
import { Practice } from '../../../core/models/practice';

@Component({
  selector: 'em-practices',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    TranslatePipe,
    EmPermissionDirective,
  ],
  providers: [ConfirmationService, TrackedDialogService],
  templateUrl: './practices.component.html',
})
export class PracticesComponent implements OnInit {
  practices: Practice[] = [];
  ref: DynamicDialogRef | undefined;
  translations: any = {};
  Permission = Permission;

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
    private practicesState: PracticesStateService,
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.practicesState.practices$.subscribe((list) => (this.practices = list));
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.practices.addPractice.title',
        'layout.configurations.practices.addPractice.successMessage.title',
        'layout.configurations.practices.addPractice.successMessage.detail',
        'layout.configurations.practices.editPractice.title',
        'layout.configurations.practices.editPractice.successMessage.title',
        'layout.configurations.practices.editPractice.successMessage.detail',
        'layout.configurations.practices.deletePractice.title',
        'layout.configurations.practices.deletePractice.message',
        'layout.configurations.practices.deletePractice.yes',
        'layout.configurations.practices.deletePractice.no',
        'layout.configurations.practices.deletePractice.successMessage.title',
        'layout.configurations.practices.deletePractice.successMessage.detail',
        'layout.configurations.practices.deletePractice.infoMessage.title',
        'layout.configurations.practices.deletePractice.infoMessage.detail',
      ])
      .subscribe((t) => (this.translations = t));
  }

  onAdd() {
    this.ref = this.dialogService.open(PracticesFormComponent, {
      header: this.translations['layout.configurations.practices.addPractice.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
      data: undefined,
    });

    this.ref.onClose.subscribe((result: { name: string } | undefined) => {
      if (result?.name) {
        this.practicesState.add(result.name).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translations['layout.configurations.practices.addPractice.successMessage.title'],
              detail: this.translations['layout.configurations.practices.addPractice.successMessage.detail'],
            });
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translations['layout.configurations.practices.addPractice.successMessage.title'],
              detail: error.error?.message || this.translate.instant('layout.configurations.practices.addPractice.errorMessage.detail'),
            });
          },
        });
      }
    });
  }

  onEdit(practice: Practice) {
    this.ref = this.dialogService.open(PracticesFormComponent, {
      header: this.translations['layout.configurations.practices.editPractice.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
      data: { id: practice.id, name: practice.name },
    });

    this.ref.onClose.subscribe((result: { name: string } | undefined) => {
      if (!result) return;
      if (result.name === practice.name) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
          detail: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage'),
        });
        return;
      }
      this.practicesState.update(practice.id, result.name).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translations['layout.configurations.practices.editPractice.successMessage.title'],
            detail: this.translations['layout.configurations.practices.editPractice.successMessage.detail'],
          });
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translations['layout.configurations.practices.editPractice.successMessage.title'],
            detail: error.error?.message || this.translate.instant('layout.configurations.practices.editPractice.errorMessage.detail'),
          });
        },
      });
    });
  }

  onDelete(event: Event, id: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.practices.deletePractice.message'],
      icon: 'pi pi-info-circle',
      header: this.translations['layout.configurations.practices.deletePractice.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.practices.deletePractice.yes'],
      rejectLabel: this.translations['layout.configurations.practices.deletePractice.no'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.practicesState.delete(id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translations['layout.configurations.practices.deletePractice.successMessage.title'],
              detail: this.translations['layout.configurations.practices.deletePractice.successMessage.detail'],
            });
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translations['layout.configurations.practices.deletePractice.successMessage.title'],
              detail: error.error?.message || this.translate.instant('layout.configurations.practices.deletePractice.errorMessage.failed'),
            });
          },
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translations['layout.configurations.practices.deletePractice.infoMessage.title'],
          detail: this.translations['layout.configurations.practices.deletePractice.infoMessage.detail'],
          life: 3000,
        });
      },
    });
  }
}
